// beehiiv Ad Network pixel — signup-conversion tracking for newsletter ads.
//
// The base pixel + pageview lives on the marketing site (landing/), gated
// behind marketing-cookie consent. When a visitor arrives from a beehiiv
// newsletter ad and accepts marketing cookies, that pixel writes the
// first-party `_bhc` attribution cookie on `.catchagent.ai` (ad-network
// placement / subscriber identifiers), which is readable here on
// app.catchagent.ai.
//
// We fire the `start_trial` conversion only when `_bhc` is present. Its presence
// implies both (1) the user actually came from a beehiiv newsletter ad and
// (2) they consented to marketing cookies on the landing site. Without it
// there is nothing to attribute and no consent, so we never load the pixel —
// the authenticated app stays free of ad trackers for everyone else.

// This pixel ID is also hardcoded in the landing-site base pixel
// (landing/src/layouts/Layout.astro). The two live in separate apps, so a shared
// constant isn't trivial; if the ID ever rotates, update both.
const BEEHIIV_PIXEL_ID = '7ab9c686-311b-4a63-800d-8972934546ff'
const BEEHIIV_PIXEL_SRC =
  'https://beehiiv-adnetwork-production.s3.amazonaws.com/pixel-v2.js'

// Conversion event name. Must EXACTLY match the event configured in beehiiv's
// campaign dashboard — beehiiv silently ignores anything else ("our system
// doesn't recognize it otherwise", per beehiiv ad-ops). A genuine new signup is
// the trial-start moment for Catch, so the campaign tracks `start_trial`.
const BEEHIIV_SIGNUP_EVENT = 'start_trial'

// The base pixel runs on the canonical `www` host. beehiiv's docs call for the
// originating host on cross-subdomain conversions so the conversion fired here
// (app.catchagent.ai) is attributed to that pixel context. This is a prod-only
// assumption, but harmless off prod: the pixel only loads when `_bhc` is present
// (see below), which is set solely by the prod landing pixel. Confirm with the
// beehiiv ad-ops contact that conversions are logging before relying on this.
const BEEHIIV_ATTRIBUTION_HOST = 'www'

// Conversion value payload for beehiiv's value-based (ROAS / predicted-LTV)
// reporting. A trial start bills nothing up front, so `value_cents` is 0;
// `predicted_ltv_cents` is a deliberate placeholder (0) until we wire a real LTV
// estimate. These fields feed value reporting only — beehiiv counts the
// conversion regardless of whether they are present.
interface BeehiivConversionData {
  currency: string
  value_cents: number
  predicted_ltv_cents: number
}

const BEEHIIV_CONVERSION_DATA: BeehiivConversionData = {
  currency: 'usd',
  value_cents: 0,
  predicted_ltv_cents: 0,
}

// The pixel only ever fires on the production app host (the `_bhc` gate below is
// set solely by the prod landing pixel). We treat any other host as non-prod and
// enable beehiiv's console `debug` logging there, so a seeded `_bhc` cookie lets
// the conversion be watched in the console while testing; prod stays silent.
// Host-based to match analytics/posthog.ts and avoid an import.meta.env cast.
const BEEHIIV_PROD_HOST = 'app.catchagent.ai'

function isBeehiivDebugEnabled(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.location.hostname !== BEEHIIV_PROD_HOST
  )
}

type BhpxArgs = readonly unknown[]

// Minimal shape of beehiiv's global. Before pixel-v2.js loads, calls queue;
// once loaded the script attaches `callMethod` and drains the queue.
interface Bhpx {
  (...args: BhpxArgs): void
  callMethod?: (...args: BhpxArgs) => void
  queue: BhpxArgs[]
  push: Bhpx
  loaded: boolean
  version: string
}

type BhpxWindow = Window & {
  bhpx?: Bhpx
  _bhpx?: Bhpx
}

// `_bhc` (incl. host-scoped `_bhc_<host>`) is set by the landing-site pixel only
// after a newsletter-ad arrival + marketing consent. Its presence is therefore
// our combined attribution + consent gate.
//
// LOAD-BEARING: this relies on beehiiv writing `_bhc` on the parent
// `.catchagent.ai` domain (not host-only on `www`) so it is readable here on
// app.catchagent.ai. If it were host-scoped this gate would always be false and
// the `start_trial` conversion would silently never fire — verify the cookie's `Domain`
// attribute in prod once the landing pixel ships. The prefix match is
// intentional for the host-scoped `_bhc_<host>` variant.
function hasBeehiivAttributionCookie(): boolean {
  return document.cookie
    .split(';')
    .some((entry) => entry.trim().startsWith('_bhc'))
}

// Install beehiiv's loader stub and inject pixel-v2.js (idempotent). Mirrors
// the official snippet: a queueing function that the loaded script later drains
// via `callMethod`.
function ensureBhpxLoaded(w: BhpxWindow): Bhpx {
  const existing = w.bhpx
  if (existing) return existing

  const queue: BhpxArgs[] = []
  const stub = ((...args: BhpxArgs): void => {
    const current = w.bhpx
    if (current?.callMethod) {
      // Method call keeps `this` bound to the bhpx object, matching the
      // official stub's `callMethod.apply(bhpx, args)`.
      current.callMethod(...args)
    } else {
      queue.push(args)
    }
  }) as Bhpx
  stub.queue = queue
  stub.push = stub
  stub.loaded = true
  stub.version = '1.0'

  w.bhpx = stub
  w._bhpx ??= stub

  const script = document.createElement('script')
  script.async = true
  script.type = 'module'
  script.src = BEEHIIV_PIXEL_SRC
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  return stub
}

let signupTracked = false

// Cross-session, once-per-user dedupe. The backend's `reactivated=1` signal is
// NOT one-shot — it is set on every login until onboarding completes — and a
// brand-new user who abandons onboarding would otherwise fire on `is_new_user=1`
// and then again on the next `reactivated=1` login. beehiiv counts each fire, so
// for a value/ROAS conversion we persist a per-user marker to keep `start_trial`
// to a single conversion per user (matching the original account-creating-only
// guarantee).
const BEEHIIV_SIGNUP_DEDUPE_PREFIX = 'beehiiv_start_trial_fired:'

function signupAlreadyTracked(userId: string): boolean {
  try {
    return (
      window.localStorage.getItem(BEEHIIV_SIGNUP_DEDUPE_PREFIX + userId) !== null
    )
  } catch {
    // localStorage can throw (private mode / blocked storage). Fall back to the
    // in-page guard rather than blocking the conversion outright.
    return false
  }
}

function markSignupTracked(userId: string): void {
  try {
    window.localStorage.setItem(BEEHIIV_SIGNUP_DEDUPE_PREFIX + userId, '1')
  } catch {
    // Best-effort persistence; ignore storage failures.
  }
}

/**
 * Fire the beehiiv `start_trial` conversion for a newsletter-driven new signup
 * or a reactivated user's return.
 *
 * No-op unless the `_bhc` attribution cookie is present, so the ad pixel is
 * never loaded for users who didn't arrive from a beehiiv newsletter ad (or who
 * declined marketing cookies on the landing site). Dedupes within the page and
 * once-per-user across sessions (keyed on `userId` in localStorage), so it fires
 * at most one conversion per user even though the backend's `reactivated` signal
 * is not one-shot. Call this only on a genuine new signup or reactivation.
 */
export function trackBeehiivSignup(userId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (signupTracked || !hasBeehiivAttributionCookie()) return
  if (signupAlreadyTracked(userId)) return

  signupTracked = true
  markSignupTracked(userId)
  const w = window as BhpxWindow
  const bhpx = ensureBhpxLoaded(w)
  bhpx('init', BEEHIIV_PIXEL_ID, {
    // The authenticated SPA must not have its in-app route changes tracked by
    // an ad pixel — we only want the single conversion below.
    trackClientNavigation: false,
    // Enabled on non-prod hosts so beehiiv logs init/track to the console.
    debug: isBeehiivDebugEnabled(),
  })
  bhpx('track', BEEHIIV_SIGNUP_EVENT, {
    host: BEEHIIV_ATTRIBUTION_HOST,
    data: BEEHIIV_CONVERSION_DATA,
  })
}
